/**
 * Final Unified Chat Hook - Phase 3 Complete
   // ✅ Phase 1: Enhanced message state management with reducer pattern
  // Replaced complex useMessageState with clean, predictable reducer
  // Key benefits: immutable updates, better performance, no dependency loops
  const { 
    messages, 
    operations: messageOps,
    findMessage,
    isStreamingAnyMessage,
    // 🆕 NEW: Multi-message run utilities
    getMessagesByRunId,
    isRunComplete,
    getRunProgress
  } = useMessageReducer();s replaces both useChat.ts and useChatEnhanced.ts with a single,
 * optimized implementation using the centralized SignalR manager.
 * 
 * Features:
 * - Centralized SignalR management via signalRManager
 * - Trusts SignalRManager's intelligent deduplication (no manual deduplication)
 * - Clean component lifecycle handling
 * - Comprehensive error handling and recovery
 * - Timeline integration for activity tracking
 * - Complete API compatibility with legacy hooks
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMessageReducer } from './useMessageReducer'; // ✅ Phase 1: Use new simplified message reducer
import { useChatSessions } from './useChatSessions'; // ✅ Phase 2: Use new session management hook
import { useChatSignalR } from './useChatSignalR'; // ✅ Phase 3: Use new SignalR event management hook
import { useNewSessionContent } from './useNewSessionContent'; // Import for welcome messages
import { ChatSession, ChatMessage } from '../entities';
import { chatService } from '../services';
import { UseChatCoreReturn } from '../types/hooks';
import { isAuthorizationError } from '../types/errors';

export const useChat = (): UseChatCoreReturn => {
  const { isAuthenticated, user } = useAuth();
  
  // ✅ Phase 2: Use dedicated session management hook
  const sessionHook = useChatSessions();
  const {
    sessions,
    currentSession,
    currentSessionId,
    loadingSessions,
    pendingNewSession,
    loadSessions,
    createSession,
    createAndSelectSession,
    selectSession,
    startNewSession,
    deleteSession,
    clearSessions,
    refreshSessions,
    updateSessionFromSignalR,
    refreshSessionData
  } = sessionHook;
  
  // Keep only essential refs for stable access
  const currentSessionIdRef = useRef<string | null>(null);
  
  // ✅ Phase 1 Complete: Enhanced message state management with reducer pattern
  // Replaced complex useMessageState with clean, predictable reducer
  // Key benefits: immutable updates, better performance, no dependency loops
  const { 
    messages, 
    operations: messageOps,
    findMessage 
    // ✅ REMOVED: isStreamingAnyMessage - no longer needed for typing detection
  } = useMessageReducer();

  // Get new session content for welcome messages
  const { content: newSessionContent } = useNewSessionContent();

  // ✅ Phase 1 Compatibility Layer: Combine operations with findMessage 
  // This maintains backward compatibility while keeping operations stable
  // The separation prevents infinite re-renders caused by findMessage dependencies
  const combinedMessageOps = useMemo(() => ({
    ...messageOps,
    findMessage
  }), [messageOps, findMessage]);

  // ✅ Phase 1 Performance Fix: Use refs for stable access in event handlers
  // This prevents dependency loops in useEffect hooks that were causing
  // "Maximum update depth exceeded" errors
  const messageOpsRef = useRef(combinedMessageOps);
  
  // Update refs when values change
  currentSessionIdRef.current = currentSessionId;
  messageOpsRef.current = combinedMessageOps;
  
  // Legacy state (keeping for compatibility)
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Status message state (for display only, not for typing indicators)
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  // 🆕 SIMPLIFIED: Single, reliable typing state based on SignalR events
  const [isTyping, setIsTyping] = useState(false);
  
  // 🆕 NEW: Multi-message run state tracking (keep for progress display)
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [runProgress, setRunProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  
  // ✅ REMOVED: Complex isRunning state and isStreamingAnyMessage dependencies
  // Now isTyping is managed purely by SignalR events: status=received → true, complete → false
  
  // ✅ NEW: Track when we're sending a message to prevent loadMessages from clearing state
  const [sendingMessage, setSendingMessage] = useState(false);
  const sendingMessageRef = useRef(false);
  
  // ✅ Track streaming completion to ignore late-arriving status events
  const streamingCompleteRef = useRef(false);
  
  // Authorization error state - throw during render for error boundary
  const [authError, setAuthError] = useState<Error | null>(null);
  if (authError) {
    throw authError;
  }
  
  // Keep sendingMessageRef in sync
  useEffect(() => {
    sendingMessageRef.current = sendingMessage;
  }, [sendingMessage]);

  // Update ref when currentSessionId changes
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // ✅ Phase 3: Use dedicated SignalR event management hook
  // Use stable callbacks to prevent re-subscriptions
  const handleStreamingUpdate = useCallback((_messageId: string, isComplete: boolean, metadata?: any) => {
    // Handle multi-message run state updates (for progress display)
    if (metadata?.runId) {
      setCurrentRunId(metadata.runId);
      
      // Update run progress based on metadata
      if (metadata.totalMessages && metadata.messageIndex) {
        setRunProgress({ 
          current: metadata.messageIndex, 
          total: metadata.totalMessages 
        });
      }
    }
    
    // ✅ SIMPLIFIED: Only clear typing on complete with explicit last message flag
    if (isComplete && (!metadata || metadata.isLastMessage !== false)) {
      // Mark streaming as complete to ignore any late-arriving status events
      streamingCompleteRef.current = true;
      setIsTyping(false);
      setStatusMessage(null); // Clear status message immediately on completion
      setCurrentRunId(null);
      setRunProgress({ current: 0, total: 0 });
    }
  }, []);

  const handleStatusUpdate = useCallback((status: string | null, _metadata?: any) => {
    // Ignore late-arriving status events if streaming has already completed
    if (streamingCompleteRef.current) {
      return;
    }
    
    // Set typing indicator based on status events
    // Backend sends: "thinking", "thinking.", "thinking..", "processing", "processing.", "streaming"
    if (status) {
      const isActiveStatus = 
        status.includes('thinking') || 
        status.includes('processing') || 
        status.includes('streaming') ||
        status.includes('received');
      
      if (isActiveStatus) {
        setIsTyping(true);
      }
    }
    
    // Update the status message for display only
    setStatusMessage(status);
  }, []);

  // 🆕 NEW: Handler for multi-message run updates
  const handleMultiMessageRunUpdate = useCallback((runId: string, isLastMessage: boolean, messageIndex: number) => {
    setCurrentRunId(runId);
    
    // Update progress tracking
    setRunProgress(prev => ({
      ...prev,
      current: Math.max(prev.current, messageIndex)
    }));
    
    // Clear run state when complete
    if (isLastMessage) {
      setTimeout(() => {
        setCurrentRunId(null);
        setRunProgress({ current: 0, total: 0 });
      }, 1000); // Clear after 1 second delay
    }
  }, []);

  const handleSessionUpdate = useCallback((data: any) => {
    updateSessionFromSignalR(data);
    
    // Handle session processing state changes
    if (data.isProcessing !== undefined) {
      if (!data.isProcessing) {
        // Processing complete - clear typing state and status
        setIsTyping(false);
        setStatusMessage(null);
      }
    }
  }, [updateSessionFromSignalR]);

  const signalRHook = useChatSignalR(
    currentSessionId,
    combinedMessageOps,
    {
      onStreamingUpdate: handleStreamingUpdate,
      onStatusUpdate: handleStatusUpdate,
      onSessionUpdate: handleSessionUpdate,
      onMultiMessageRunUpdate: handleMultiMessageRunUpdate
    }
  );

  const { isConnected, updateSessionContext, clearContext } = signalRHook;

  // ✅ NEW: Update SignalR context when session changes (including auto-selection)
  useEffect(() => {
    if (currentSessionId) {
      updateSessionContext(currentSessionId);
    } else {
      clearContext();
    }
  }, [currentSessionId, updateSessionContext, clearContext]);

  const loadMessages = useCallback(async (sessionId: string) => {
    if (!sessionId) return;

    setLoadingMessages(true);
    setError(null);

    try {
      // Load at most 50 latest messages when selecting a session
      const response = await chatService.getLatestMessages(sessionId, 50);
      const messageInstances = response.messages.map((msg: any) => new ChatMessage(msg));
      
      // ✅ Use ref to avoid dependency loop
      messageOpsRef.current.replaceMessages(messageInstances);
    } catch (error) {
      // Store authorization errors to be thrown during render
      if (isAuthorizationError(error)) {
        // Don't log auth errors - they're expected and handled by the error boundary
        setLoadingMessages(false);
        setAuthError(error as Error);
        return;
      }
      console.error('[useChat] Failed to load messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Load messages when session changes (but not when sendingMessage changes)
  useEffect(() => {
    if (currentSessionId && !sendingMessageRef.current) {
      // Call loadMessages and catch any errors to prevent unhandled promise rejections
      // Authorization errors will be stored in state and thrown during render
      loadMessages(currentSessionId).catch((error) => {
        // Only log non-auth errors - auth errors are handled by the hook's state
        if (!isAuthorizationError(error)) {
          console.error('[useChat] Failed to load messages:', error);
        }
      });
    } else if (!currentSessionId) {
      // ✅ Use ref to avoid dependency loop
      messageOpsRef.current.clearMessages();
    }
  }, [currentSessionId, loadMessages]);

  // Create wrapper for createAndSelectSession that uses session hook
  const createAndSelectSessionWrapper = useCallback(async (): Promise<ChatSession> => {
    // Use the new atomic createAndSelectSession from useChatSessions
    const session = await createAndSelectSession();
    // ✅ NEW: Update session context for enhanced validation
    updateSessionContext(session.id);
    return session;
  }, [createAndSelectSession, updateSessionContext]);

  // Create wrapper for startNewSession that clears context
  const wrappedStartNewSession = useCallback(() => {
    startNewSession();
    // ✅ NEW: Use message operations to clear messages
    messageOps.clearMessages();
    // ✅ NEW: Clear session context for enhanced validation
    clearContext();
  }, [startNewSession, messageOps, clearContext]);

  // Create wrapper for selectSession that updates SignalR context
  const wrappedSelectSession = useCallback(async (sessionId: string) => {
    // Prevent session switching if agent is typing/responding
    if (isTyping) {
      console.warn('[useChat] Cannot switch sessions while agent is typing. Please wait for the response to complete.');
      return;
    }

    await selectSession(sessionId, isTyping);
    // ✅ NEW: Update SignalR session context for enhanced validation
    updateSessionContext(sessionId);
  }, [selectSession, updateSessionContext, isTyping]);

  const sendMessage = useCallback(async (content: string): Promise<ChatMessage> => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('Not authenticated');
    }

    setError(null);
    setSendingMessage(true); // ✅ Prevent loadMessages from clearing user message
    streamingCompleteRef.current = false; // Reset so status events are processed for new message

    try {
      let sessionId = currentSessionId;
      let isNewSession = false;

      // Create session if starting new one
      if (!sessionId || pendingNewSession) {
        const session = await createAndSelectSessionWrapper();
        sessionId = session.id;
        isNewSession = true;
      }

      // Add assistant welcome message for new sessions if content is available
      if (isNewSession && newSessionContent?.message) {
        messageOps.addAssistantMessage(newSessionContent.message, sessionId);
      }

      // ✅ CRITICAL FIX: Add user message and ensure it's immediately visible
      const userMessage = messageOps.addUserMessage(content, undefined, sessionId);
      
      // Send message to backend
      await chatService.sendMessage(sessionId, content);
      
      // ✅ IMPROVED: Use double RAF to ensure React has processed all state updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // TEMPORARILY COMMENT OUT TO TEST IF USER MESSAGE APPEARS
          /*
          // Update user message with backend ID if provided
          if (response.userMessage?.id && response.userMessage.id !== userMessage.id) {
            messageOps.updateUserMessageId(userMessage.id, response.userMessage.id);
          } else {
            // Handle case where backend doesn't return userMessage
            messageOps.updateUserMessageId(userMessage.id, userMessage.id);
          }
          */
        });
      });
      
      // ✅ SIMPLIFIED: Let status events handle typing state
      // No need to preemptively set typing here - status events will handle it

      return userMessage;
    } catch (error) {
      console.error('[useChat] Failed to send message:', error);
      setError('Failed to send message');
      throw error;
    } finally {
      setSendingMessage(false); // ✅ Reset flag when message sending is complete
    }
  }, [isAuthenticated, user?.id, currentSessionId, pendingNewSession, createAndSelectSessionWrapper, messageOps, newSessionContent]);

  // Load sessions when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Call loadSessions and catch any errors to prevent unhandled promise rejections
      // Authorization errors will be stored in state and thrown during render
      loadSessions().catch((error) => {
        // Only log non-auth errors - auth errors are handled by the hook's state
        if (!isAuthorizationError(error)) {
          console.error('[useChat] Failed to load sessions:', error);
        }
      });
    }
  }, [isAuthenticated, user?.id, loadSessions]);

  return {
    // Sessions
    sessions,
    currentSession,
    currentSessionId,
    loadingSessions,
    hasMoreSessions: sessionHook.hasMoreSessions,
    isLoadingMore: sessionHook.isLoadingMore,
    
    // Messages
    messages,
    loadingMessages,
    
    // State
    error,
    isTyping,
    statusMessage,
    pendingNewSession,
    
    // 🆕 NEW: Multi-message run state
    currentRunId,
    runProgress,
    getMessagesByRunId: (runId: string) => {
      return messages.filter(m => m.metadata?.runId === runId);
    },
    isRunComplete: (runId: string) => {
      const runMessages = messages.filter(m => m.metadata?.runId === runId);
      if (runMessages.length === 0) return false;
      
      const lastMessage = runMessages.find(m => m.metadata?.isLastMessage);
      return Boolean(lastMessage && !lastMessage.isStreaming);
    },
    
    // Actions
    createSession,
    createAndSelectSession: createAndSelectSessionWrapper,
    startNewSession: wrappedStartNewSession,
    selectSession: wrappedSelectSession,
    sendMessage,
    deleteSession,
    clearSessions,
    refreshSessions,
    refreshSessionData,
    loadMoreSessions: sessionHook.loadMoreSessions,
    
    // Real-time
    isConnected
  };
};
